import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useSession } from '@/context/SessionContext';
import { supabase } from '@/lib/supabase/client';

export interface FavoritedProduct {
  id: string;
  title: string;
  description: string;
  fixed_sale_price: number;
  stock: number;
  addedAt: number;
  store_id?: string;
  store_name?: string;
  main_image_url?: string | null;
  cover_url?: string | null;
  image_urls?: string[];
}

const FAVORITES_STORAGE_KEY = 'minibid_favorites';

interface FavoritesContextValue {
  favorites: Map<string, FavoritedProduct>;
  isFavorited: (productId: string) => boolean;
  addFavorite: (product: FavoritedProduct) => void;
  removeFavorite: (productId: string) => void;
  toggleFavorite: (product: FavoritedProduct) => void;
  getFavoritesList: () => FavoritedProduct[];
  clearFavorites: () => void;
  isLoaded: boolean;
  isSyncing: boolean;
  isAuthenticated: boolean;
  loadLoggedInFavorites: () => Promise<Map<string, FavoritedProduct>>;
  loadLocalFavorites: () => Map<string, FavoritedProduct>;
  hasLocalFavoritesToSync: boolean;
  syncLocalFavoritesManually: (localFavs: Map<string, FavoritedProduct>) => Promise<boolean>;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);
let hasWarnedMissingFavoritesProvider = false;

const FAVORITES_FALLBACK: FavoritesContextValue = {
  favorites: new Map<string, FavoritedProduct>(),
  isFavorited: () => false,
  addFavorite: () => {},
  removeFavorite: () => {},
  toggleFavorite: () => {},
  getFavoritesList: () => [],
  clearFavorites: () => {},
  isLoaded: true,
  isSyncing: false,
  isAuthenticated: false,
  loadLoggedInFavorites: async () => new Map<string, FavoritedProduct>(),
  loadLocalFavorites: () => new Map<string, FavoritedProduct>(),
  hasLocalFavoritesToSync: false,
  syncLocalFavoritesManually: async () => false,
};

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [favorites, setFavorites] = useState<Map<string, FavoritedProduct>>(new Map());
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [hasLocalFavoritesToSync, setHasLocalFavoritesToSync] = useState(false);
  const prevFavoritesRef = useRef<Map<string, FavoritedProduct>>(new Map());
  const syncInProgressRef = useRef(false);

  // Load local favorites from localStorage
  const loadLocalFavorites = useCallback(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Map<string, FavoritedProduct>(parsed);
      }
    } catch (err) {
      console.error('[FavoritesContext] Error loading local favorites:', err);
    }
    return new Map<string, FavoritedProduct>();
  }, []);

  // Load favorites from database for logged-in users
  const loadLoggedInFavorites = useCallback(async () => {
    if (!session?.user?.id) return new Map<string, FavoritedProduct>();

    try {
      const { data, error } = await supabase
        .from('favorites')
        .select('*')
        .eq('user_id', session.user.id)
        .order('added_at', { ascending: false });

      if (error) {
        console.error('[FavoritesContext] Error loading database favorites:', error);
        return new Map<string, FavoritedProduct>();
      }

      if (data && data.length > 0) {
        return new Map<string, FavoritedProduct>(
          data.map((fav: any) => [
            fav.product_id,
            {
              id: fav.product_id,
              title: fav.title || 'Sin título',
              description: fav.description || '',
              fixed_sale_price: fav.fixed_sale_price || 0,
              stock: fav.stock || 0,
              addedAt: typeof fav.added_at === 'string' ? new Date(fav.added_at).getTime() : (fav.added_at || Date.now()),
              store_id: fav.store_id,
              store_name: fav.store_name,
              main_image_url: fav.main_image_url || null,
              cover_url: fav.cover_url || null,
              image_urls: Array.isArray(fav.image_urls) ? fav.image_urls : [],
            },
          ])
        );
      }
    } catch (err) {
      console.error('[FavoritesContext] Error loading database favorites:', err);
    }
    return new Map<string, FavoritedProduct>();
  }, [session?.user?.id]);

  // Sync incremental changes to database
  const syncIncrementalChanges = useCallback(
    async (currentFavorites: Map<string, FavoritedProduct>) => {
      if (!session?.user?.id) return;

      const prevFavorites = prevFavoritesRef.current;
      const added: FavoritedProduct[] = [];
      const removed: string[] = [];

      for (const [id, product] of currentFavorites) {
        if (!prevFavorites.has(id)) added.push(product);
      }
      for (const [id] of prevFavorites) {
        if (!currentFavorites.has(id)) removed.push(id);
      }

      if (added.length === 0 && removed.length === 0) return;

      // Update ref immediately so rapid toggles don't cause double-insert
      prevFavoritesRef.current = new Map(currentFavorites);

      try {
        if (added.length > 0) {
          const { error: insertError } = await supabase.from('favorites').insert(
            added.map((fav) => ({
              user_id: session.user.id,
              product_id: fav.id,
              title: fav.title,
              description: fav.description,
              fixed_sale_price: fav.fixed_sale_price,
              stock: fav.stock,
              store_id: fav.store_id,
              store_name: fav.store_name,
              added_at: fav.addedAt,
            }))
          );
          if (insertError) {
            console.error('[FavoritesContext] Error inserting favorites:', insertError);
          }
        }

        if (removed.length > 0) {
          const { error: deleteError } = await supabase
            .from('favorites')
            .delete()
            .eq('user_id', session.user.id)
            .in('product_id', removed);
          if (deleteError) {
            console.error('[FavoritesContext] Error deleting favorites:', deleteError);
          }
        }
      } catch (err) {
        console.error('[FavoritesContext] Error syncing incremental changes:', err);
      }
    },
    [session?.user?.id]
  );

  // Subscribe to real-time changes from Supabase (for multi-tab / multi-device sync)
  useEffect(() => {
    if (!session?.user?.id) return;

    const subscription = supabase
      .channel(`favorites-ctx:${session.user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'favorites',
          filter: `user_id=eq.${session.user.id}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newFav = payload.new as any;
            setFavorites((prev) => {
              const next = new Map(prev);
              next.set(newFav.product_id, {
                id: newFav.product_id,
                title: newFav.title,
                description: newFav.description,
                fixed_sale_price: newFav.fixed_sale_price,
                stock: newFav.stock,
                addedAt: typeof newFav.added_at === 'string' ? new Date(newFav.added_at).getTime() : newFav.added_at,
                store_id: newFav.store_id,
                store_name: newFav.store_name,
                main_image_url: newFav.main_image_url || null,
                cover_url: newFav.cover_url || null,
                image_urls: Array.isArray(newFav.image_urls) ? newFav.image_urls : [],
              });
              prevFavoritesRef.current = new Map(next);
              return next;
            });
          } else if (payload.eventType === 'DELETE') {
            const oldFav = payload.old as any;
            setFavorites((prev) => {
              const next = new Map(prev);
              next.delete(oldFav.product_id);
              prevFavoritesRef.current = new Map(next);
              return next;
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedFav = payload.new as any;
            setFavorites((prev) => {
              const next = new Map(prev);
              next.set(updatedFav.product_id, {
                id: updatedFav.product_id,
                title: updatedFav.title,
                description: updatedFav.description,
                fixed_sale_price: updatedFav.fixed_sale_price,
                stock: updatedFav.stock,
                addedAt: typeof updatedFav.added_at === 'string' ? new Date(updatedFav.added_at).getTime() : updatedFav.added_at,
                store_id: updatedFav.store_id,
                store_name: updatedFav.store_name,
                main_image_url: updatedFav.main_image_url || null,
                cover_url: updatedFav.cover_url || null,
                image_urls: Array.isArray(updatedFav.image_urls) ? updatedFav.image_urls : [],
              });
              prevFavoritesRef.current = new Map(next);
              return next;
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [session?.user?.id]);

  // Listen to localStorage changes for offline sync (cross-tab)
  useEffect(() => {
    if (session?.user?.id) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === FAVORITES_STORAGE_KEY && e.newValue) {
        try {
          const updated = new Map<string, FavoritedProduct>(JSON.parse(e.newValue));
          setFavorites(updated);
          prevFavoritesRef.current = new Map(updated);
        } catch (err) {
          console.error('[FavoritesContext] Error handling localStorage change:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [session?.user?.id]);

  // Initialize favorites on mount or when session changes
  useEffect(() => {
    let cancelled = false;

    const initializeFavorites = async () => {
      if (session?.user?.id) {
        // Load database favorites for logged-in user
        const dbFavorites = await loadLoggedInFavorites();
        
        // Check if there are local favorites to sync that are NOT in DB yet
        const localFavs = loadLocalFavorites();
        if (localFavs.size > 0) {
          // Verify if there are ANY products in local that are NOT in DB
          const hasNewLocalFavs = Array.from(localFavs.keys()).some(id => !dbFavorites.has(id));
          
          if (hasNewLocalFavs) {
            if (!cancelled) {
              setHasLocalFavoritesToSync(true);
            }
          } else {
            // If local favorites are already in DB, just clean up local storage
            localStorage.removeItem(FAVORITES_STORAGE_KEY);
            if (!cancelled) {
              setHasLocalFavoritesToSync(false);
            }
          }
        }
        
        if (!cancelled) {
          setFavorites(dbFavorites);
          prevFavoritesRef.current = new Map(dbFavorites);
        }
      } else {
        // Load local favorites for anonymous user
        const localFavs = loadLocalFavorites();
        if (!cancelled) {
          setFavorites(localFavs);
          prevFavoritesRef.current = new Map(localFavs);
          setHasLocalFavoritesToSync(false);
        }
      }
      if (!cancelled) setIsLoaded(true);
    };

    initializeFavorites();
    return () => { cancelled = true; };
  }, [session?.user?.id, loadLocalFavorites, loadLoggedInFavorites]);

  // Save to localStorage and sync to DB on every change
  // NOTE: isSyncing is intentionally NOT in deps — use a ref to prevent concurrent syncs
  useEffect(() => {
    if (!isLoaded) return;

    // Always persist to localStorage
    try {
      const serialized = Array.from(favorites.entries());
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(serialized));
    } catch (err) {
      console.error('[FavoritesContext] Error saving to localStorage:', err);
    }

    // Sync to DB for logged-in users, preventing concurrent sync calls
    if (session?.user?.id && !syncInProgressRef.current) {
      syncInProgressRef.current = true;
      setIsSyncing(true);
      syncIncrementalChanges(favorites).finally(() => {
        syncInProgressRef.current = false;
        setIsSyncing(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favorites, isLoaded, session?.user?.id]);

  const isFavorited = useCallback((productId: string) => favorites.has(productId), [favorites]);

  const addFavorite = useCallback((product: FavoritedProduct) => {
    setFavorites((prev) => new Map(prev).set(product.id, { ...product, addedAt: Date.now() }));
  }, []);

  const removeFavorite = useCallback((productId: string) => {
    setFavorites((prev) => {
      const next = new Map(prev);
      next.delete(productId);
      return next;
    });
  }, []);

  const toggleFavorite = useCallback((product: FavoritedProduct) => {
    setFavorites((prev) => {
      const next = new Map(prev);
      if (next.has(product.id)) {
        next.delete(product.id);
      } else {
        next.set(product.id, { ...product, addedAt: Date.now() });
      }
      return next;
    });
  }, []);

  const getFavoritesList = useCallback(
    () => Array.from(favorites.values()).sort((a, b) => b.addedAt - a.addedAt),
    [favorites]
  );

  const clearFavorites = useCallback(() => setFavorites(new Map()), []);

  // Sync local favorites manually to database and merge with existing
  const syncLocalFavoritesManually = useCallback(
    async (localFavs: Map<string, FavoritedProduct>) => {
      if (!session?.user?.id || localFavs.size === 0) {
        return false;
      }

      try {
        setIsSyncing(true);
        
        // Convert Map to array for insertion
        const favoritesArray = Array.from(localFavs.values()).map(fav => ({
          user_id: session.user.id,
          product_id: fav.id,
          title: fav.title,
          description: fav.description,
          fixed_sale_price: fav.fixed_sale_price,
          stock: fav.stock,
          store_id: fav.store_id,
          store_name: fav.store_name,
          added_at: fav.addedAt,
        }));

        // Upsert favorites - ON CONFLICT DO NOTHING skips duplicates
        const { error } = await supabase
          .from('favorites')
          .upsert(favoritesArray, {
            onConflict: 'user_id,product_id',
            ignoreDuplicates: true,
          });

        if (error) {
          console.error('[FavoritesContext] Error syncing local favorites:', error);
          return false;
        }

        // Load updated favorites from database (includes the synced ones)
        const updatedDbFavorites = await loadLoggedInFavorites();
        
        // Update favorites state with merge result
        setFavorites(updatedDbFavorites);
        prevFavoritesRef.current = new Map(updatedDbFavorites);
        
        // Clear localStorage after successful sync
        localStorage.removeItem(FAVORITES_STORAGE_KEY);
        setHasLocalFavoritesToSync(false);
        
        // Force update localStorage for session persistence
        const serialized = Array.from(updatedDbFavorites.entries());
        localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(serialized));
        
        return true;
      } catch (err) {
        console.error('[FavoritesContext] Error syncing local favorites:', err);
        return false;
      } finally {
        setIsSyncing(false);
      }
    },
    [session?.user?.id, loadLoggedInFavorites]
  );

  const value: FavoritesContextValue = {
    favorites,
    isFavorited,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    getFavoritesList,
    clearFavorites,
    isLoaded,
    isSyncing,
    isAuthenticated: !!session?.user?.id,
    loadLoggedInFavorites,
    loadLocalFavorites,
    hasLocalFavoritesToSync,
    syncLocalFavoritesManually,
  };

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites(): FavoritesContextValue {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    if (!hasWarnedMissingFavoritesProvider) {
      hasWarnedMissingFavoritesProvider = true;
      console.error('useFavorites called without FavoritesProvider; using safe fallback.');
    }
    return FAVORITES_FALLBACK;
  }
  return ctx;
}
